import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, ImageOff, ZoomIn } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import { LAVORI } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const LavoriCategory = ({ category }) => {
  const { lang, t } = useLang();
  const data = LAVORI[category];

  // flatten photos that have images, for lightbox navigation
  const gallery = [];
  data.groups.forEach((g) =>
    g.photos.forEach((p) => {
      if (p.img) gallery.push({ ...p, client: g.client, location: g.location });
    })
  );

  const [lb, setLb] = useState(null);
  const close = useCallback(() => setLb(null), []);
  const next = useCallback(() => setLb((i) => (i === null ? i : (i + 1) % gallery.length)), [gallery.length]);
  const prev = useCallback(() => setLb((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length)), [gallery.length]);
  const openByImg = (img) => {
    const idx = gallery.findIndex((g) => g.img === img);
    if (idx >= 0) setLb(idx);
  };

  useEffect(() => {
    if (lb === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lb, close, next, prev]);

  return (
    <div data-testid={`lavori-${category}`}>
      <PageHeader label={t("lavori.label")} title={data[lang].title} intro={data[lang].intro} tone="dark" />

      <section className="relative bg-forest-deep text-cream pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 space-y-16">
          {data.groups.map((g, gi) => (
            <div key={gi} data-testid={`project-group-${gi}`}>
              <Reveal>
                <div className="flex flex-wrap items-end justify-between gap-2 border-b border-cream/15 pb-4 mb-8">
                  <h3 className="font-display text-3xl sm:text-4xl font-medium">{g.client}</h3>
                  <span className="text-sm uppercase tracking-widest text-gold">{g.location}</span>
                </div>
              </Reveal>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                {g.photos.map((p, pi) => (
                  <motion.div
                    key={pi}
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: EASE, delay: pi * 0.06 }}
                  >
                    {p.img ? (
                      <button
                        onClick={() => openByImg(p.img)}
                        data-testid={`photo-${category}-${gi}-${pi}`}
                        className="group block w-full text-left"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden rounded-tr-2xl rounded-bl-2xl bg-forest-surface">
                          <img
                            src={p.img}
                            alt={p[lang]}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-forest-deep/0 group-hover:bg-forest-deep/30 transition-colors duration-500 grid place-items-center">
                            <ZoomIn className="text-cream opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={26} />
                          </div>
                        </div>
                        <p className="text-sm text-cream/70 mt-3 group-hover:text-gold transition-colors">{p[lang]}</p>
                      </button>
                    ) : (
                      <div data-testid={`photo-soon-${category}-${gi}-${pi}`}>
                        <div className="relative aspect-[4/3] overflow-hidden rounded-tr-2xl rounded-bl-2xl bg-forest-surface/60 border border-dashed border-cream/20 grid place-items-center">
                          <div className="flex flex-col items-center gap-2 text-cream/40">
                            <ImageOff size={24} />
                            <span className="text-[0.65rem] uppercase tracking-widest">{t("lavori.soon")}</span>
                          </div>
                        </div>
                        <p className="text-sm text-cream/50 mt-3">{p[lang]}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lb !== null && gallery[lb] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-forest-deep/97 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={close}
            data-testid="lightbox"
          >
            <button onClick={close} className="absolute top-6 right-6 text-cream/70 hover:text-gold z-10" data-testid="lightbox-close" aria-label="Close">
              <X size={32} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 grid place-items-center w-12 h-12 rounded-full border border-cream/25 text-cream hover:border-gold hover:text-gold z-10" data-testid="lightbox-prev" aria-label="Prev">
              <ArrowLeft size={20} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 grid place-items-center w-12 h-12 rounded-full border border-cream/25 text-cream hover:border-gold hover:text-gold z-10" data-testid="lightbox-next" aria-label="Next">
              <ArrowRight size={20} />
            </button>
            <motion.figure
              key={gallery[lb].img}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={gallery[lb].img} alt={gallery[lb][lang]} className="w-full max-h-[72vh] object-contain rounded-tr-3xl rounded-bl-3xl" />
              <figcaption className="mt-5 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-gold text-xs font-bold uppercase tracking-[0.2em]">{gallery[lb].client}</div>
                  <div className="font-display text-2xl font-light text-cream mt-1">{gallery[lb][lang]}</div>
                </div>
                <div className="text-cream/60 text-sm">{gallery[lb].location}</div>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LavoriCategory;
